import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Link } from 'react-router-dom';
import { getDiscount } from '../../utils/functions';
import { useDispatch, useSelector } from 'react-redux';
import { addToWishlist, removeFromWishlist } from '../../actions/wishlistAction';
import { addItemsToCart, removeItemsFromCart } from '../../actions/cartAction';
import { useSnackbar } from 'notistack';
import { useLang, uiText } from '../../utils/i18n';

const Product = ({ _id, name, nameZh, images, ratings, numOfReviews, price, cuttedPrice, outOfStock }) => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { lang } = useLang();
    const displayName = lang === 'zh' && nameZh ? nameZh : name;

    const { wishlistItems } = useSelector((state) => state.wishlist);
    const { cartItems } = useSelector((state) => state.cart);

    const itemInWishlist = wishlistItems.some((i) => i.product === _id);
    const cartItem = cartItems.find((i) => i.product === _id);

    const addToWishlistHandler = () => {
        if (itemInWishlist) {
            dispatch(removeFromWishlist(_id));
            enqueueSnackbar(uiText('removeFromWishlist', lang), { variant: "success" });
        } else {
            dispatch(addToWishlist(_id));
            enqueueSnackbar(uiText('addedToWishlist', lang), { variant: "success" });
        }
    }

    const addToCartHandler = () => {
        if (outOfStock) {
            enqueueSnackbar(uiText('productOutOfStock', lang), { variant: "error" });
            return;
        }
        dispatch(addItemsToCart(_id, 1));
        enqueueSnackbar(uiText('addedToCart', lang), { variant: "success" });
    }

    const increaseQty = () => {
        // No stock cap anymore — inventory counts aren't tracked, so
        // customers can order as much as they like.
        dispatch(addItemsToCart(_id, cartItem.quantity + 1));
    }

    const decreaseQty = () => {
        if (cartItem.quantity <= 1) {
            dispatch(removeItemsFromCart(_id));
            return;
        }
        dispatch(addItemsToCart(_id, cartItem.quantity - 1));
    }

    return (
        <div className="flex flex-col items-center sm:items-start gap-2 px-2 py-4 sm:px-4 sm:py-6 relative hover:shadow-lg rounded-sm border-b border-gray-100 sm:border-0">
            {/* <!-- image & product title --> */}
            <Link to={`/product/${_id}`} className="flex flex-col items-center text-center group w-full">
                <div className="w-28 h-28 sm:w-44 sm:h-48">
                    <img draggable="false" className="w-full h-full object-contain" src={images && images.length > 0 ? images[0].url : "https://placehold.co/300x300?text=No+Image"} alt="" />
                </div>
                <h2 className="text-xs sm:text-sm mt-2 sm:mt-4 group-hover:text-primary-blue text-center sm:text-left">{displayName.length > 85 ? `${displayName.substring(0, 85)}...` : displayName}</h2>
            </Link>
            {/* <!-- image & product title --> */}

            {/* <!-- product description --> */}
            <div className="flex flex-col gap-1.5 sm:gap-2 items-center sm:items-start w-full">
                {/* <!-- rating badge --> */}
                <span className="text-sm text-gray-500 font-medium flex gap-2 items-center">
                    <span className="text-xs px-1.5 py-0.5 bg-primary-green rounded-sm text-white flex items-center gap-0.5">{ratings.toFixed(1)} <StarIcon sx={{ fontSize: "14px" }} /></span>
                    <span>({numOfReviews})</span>
                </span>
                {/* <!-- rating badge --> */}

                {/* <!-- price container --> */}
                <div className="flex items-center gap-1.5 text-md font-medium">
                    <span>HK${price.toLocaleString()}</span>
                    <span className="text-gray-500 line-through text-xs">HK${cuttedPrice.toLocaleString()}</span>
                    <span className="text-xs text-primary-green">{getDiscount(price, cuttedPrice)}%&nbsp;{uiText('offLabel', lang)}</span>
                </div>
                {/* <!-- price container --> */}

                {/* <!-- add to cart / qty stepper --> */}
                {outOfStock ? (
                    <span className="text-xs text-red-500 font-medium mt-1">{uiText('outOfStock', lang)}</span>
                ) : cartItem ? (
                    <div className="flex items-center justify-between mt-1 border border-primary-blue rounded-sm w-24 sm:w-28 overflow-hidden">
                        <button onClick={decreaseQty} className="flex-1 py-1 text-primary-blue hover:bg-blue-50 flex justify-center"><RemoveIcon sx={{ fontSize: "16px" }} /></button>
                        <span className="text-sm font-medium">{cartItem.quantity}</span>
                        <button onClick={increaseQty} className="flex-1 py-1 text-primary-blue hover:bg-blue-50 flex justify-center"><AddIcon sx={{ fontSize: "16px" }} /></button>
                    </div>
                ) : (
                    <button onClick={addToCartHandler} className="mt-1 border border-primary-blue text-primary-blue text-xs font-medium uppercase px-4 py-1.5 rounded-sm hover:bg-primary-blue hover:text-white w-24 sm:w-28">
                        {uiText('addBtn', lang)}
                    </button>
                )}
                {/* <!-- add to cart / qty stepper --> */}
            </div>
            {/* <!-- product description --> */}

            {/* <!-- wishlist badge --> */}
            <span onClick={addToWishlistHandler} className={`${itemInWishlist ? "text-red-500" : "hover:text-red-500 text-gray-300"} absolute top-3 right-3 sm:top-6 sm:right-6 cursor-pointer`}><FavoriteIcon sx={{ fontSize: "18px" }} /></span>
            {/* <!-- wishlist badge --> */}

        </div>
    );
};

export default Product;